import EventEmitter from 'eventemitter3';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import ProxyContext from './context';
import { IProxyContext } from './types/context-types';
import { EventBus, ProxyEvent } from './types/proxy-event-bus-types';

import { ReactNode } from 'react';

interface ProxyProviderProps {
  children: ReactNode;
}

export const ProxyProvider: FC<ProxyProviderProps> = ({ children }) => {
  const [proxy, setProxy] = useState<boolean>(false);
  const [proxyPath, setProxyPath] = useState<string>('');
  const [proxyFrame, setProxyFrame] = useState<string>();
  const [ready, setReady] = useState<boolean>(false);
  const eventEmitter = useMemo(() => new EventEmitter(), []);

  useEffect(() => {
    const proxySegment = getProxySegment();

    if (proxySegment && proxySegment.includes('-frame')) {
      const handlePostMessage = (event: MessageEvent<EventBus>) => {
        eventEmitter.emit(event.data.messageType, event.data);
      };

      setProxy(true);
      setProxyPath(`/${proxySegment}`);
      setProxyFrame(proxySegment.replace('-frame', ''));
      window.addEventListener('message', handlePostMessage);
      setReady(true);

      return () => window.removeEventListener('message', handlePostMessage);
    } else setReady(true);
  }, []);

  const getProxySegment = () => {
    const pathSegments = window.location.pathname.split('/');
    return pathSegments[1];
  };

  const sendEvent = (data: EventBus | ProxyEvent) => {
    const inIframe = window.self !== window.top;

    inIframe
      ? window.parent.postMessage({ name: data.messageType, ...data }, '*')
      : console.error(`Cannot send '${data.messageType}' message to parent window. 
        \nParent Window Exists: ${inIframe}.`);
  };

  const sendEventToNestedIframes = useCallback((event: EventBus) => {
    const iframes = document.querySelectorAll('iframe');

    iframes.forEach((frame) => {
      frame.contentWindow?.postMessage(event, '*');
    });
  }, []);

  const createActivatedFrameUrl = (url: URL) => {
    url.searchParams.append('forceFrameActivation', 'true');
    return url;
  };

  const listenEvent = useCallback(
    (type: EventBus['messageType'], callBack: (cb: EventBus) => void) => {
      eventEmitter.on(type, callBack);
      return () => eventEmitter.off(type, callBack);
    },
    [eventEmitter]
  );

  const unlistenEvent = useCallback(
    (type: EventBus['messageType'], callBack: (cb: EventBus) => void) => {
      eventEmitter.off(type, callBack);
    },
    [eventEmitter]
  );

  const context = {
    proxy,
    proxyPath,
    proxyFrame,
    sendEvent,
    listenEvent,
    unlistenEvent,
    createActivatedFrameUrl
  } as IProxyContext;

  return <ProxyContext.Provider value={{ ...context }}>{ready ? children : null}</ProxyContext.Provider>;
};

export default ProxyProvider;
